import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { FiLock, FiPaperclip, FiSend, FiX } from "react-icons/fi";
import Button from "../../../common/Button/Button";
import { Toaster } from "../../../common/Toaster/Toaster";
import { presignUpload } from "../../../service/comms/messagesService";
import { post as commsPost } from "../../../service/comms/commsApi";
import { probeImageSize } from "../../../utils/imageProbe";
import { participantId } from "../../../utils/commsParticipantId";

/** Attachment size limits (bytes). */
const IMAGE_MAX_BYTES = 20 * 1024 * 1024;
const PDF_MAX_BYTES = 25 * 1024 * 1024;
const MAX_TEXTAREA_ROWS = 6;
const ACCEPT_TYPES = "image/*,application/pdf";

/** Presign buckets that don't already match a content_type enum member. */
const BUCKET_TO_CONTENT_TYPE = { DOCUMENT: "PDF", AUDIO: "VOICE" };

/**
 * Map a presign `content_type_bucket` onto the canonical message content_type
 * enum (TEXT|IMAGE|VIDEO|PDF|VOICE). The bucket is already uppercase in
 * practice, but normalizing keeps a lowercase variant from reaching the API.
 *
 * @param {string|undefined} bucket
 * @returns {string}
 */
const contentTypeFromBucket = (bucket) => {
  const upper = String(bucket || "").toUpperCase();
  return BUCKET_TO_CONTENT_TYPE[upper] || upper || "TEXT";
};

/**
 * Small dropdown listing selectable coach participants for @mentions.
 *
 * @param {Object} props
 * @param {Array<{ user_id: string, name: string }>} props.options - Filtered coach list.
 * @param {(opt: { user_id: string, name: string }) => void} props.onPick - Selection handler.
 */
const MentionMenu = ({ options, onPick }) => {
  if (!options.length) return null;
  return (
    <ul
      role="listbox"
      aria-label="Mention a coach"
      className="absolute bottom-full left-0 mb-1 w-56 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg z-20"
    >
      {options.map((opt) => (
        <li key={opt.user_id}>
          <button
            type="button"
            onClick={() => onPick(opt)}
            className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-orange-50"
          >
            @{opt.name}
          </button>
        </li>
      ))}
    </ul>
  );
};

/**
 * Message composer for the Flex Comms workspace. Supports client / internal-note
 * modes, @mention autocomplete, a single image/PDF attachment (presigned upload),
 * reply quoting, and optimistic-friendly submit via the parent's onSubmit.
 *
 * @param {Object} props
 * @param {boolean} [props.disabled=false] - Hard-disable (e.g. room archived).
 * @param {boolean} [props.canPublish=true] - Coach write ability (client-pause gate).
 * @param {Array<'client'|'internal'>} [props.modes] - Enabled composer modes.
 * @param {{ id: string, sender_name: string, snippet: string }|null} [props.replyTo] - Reply target.
 * @param {() => void} [props.onClearReply] - Clears the reply target.
 * @param {Array<Object>} [props.participants] - Room participants for @mentions.
 * @param {string|null} [props.bannerText] - Notice shown above the input.
 * @param {(payload: Object) => Promise<Object>} props.onSubmit - Sends the message.
 * @param {string} [props.initialText] - Draft text to seed the input with.
 * @param {number} [props.initialTextNonce] - Changes to force a re-seed even
 *   when initialText is identical to the previous draft.
 */
export function Composer({
  disabled = false,
  // Accepted but not yet enforced here — publish gating happens upstream.
  canPublish: _canPublish = true,
  modes = ["client", "internal"],
  replyTo = null,
  onClearReply,
  participants = [],
  bannerText = null,
  onSubmit,
  initialText,
  initialTextNonce,
}) {
  const [mode, setMode] = useState(modes[0] || "client");
  const [text, setText] = useState("");
  // { data_url, blob_path, file_size, content_type, mime_type, name, width?, height? }
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // string or null
  const [selectedMentions, setSelectedMentions] = useState({}); // name -> user_id
  const [toast, setToast] = useState(null); // { message, type }

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isInternal = mode === "internal";
  const hardDisabled = disabled;

  // Seed the input with an externally-provided draft (e.g. a renewal check-in
  // from the Subscriptions tab). Re-runs on nonce change so repeat drafts land.
  useEffect(() => {
    if (initialText == null || initialText === "") return;
    setText(initialText);
    setMode("client");
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialText, initialTextNonce]);

  /** Active coaches available to mention (removed excluded). */
  const coachOptions = useMemo(
    () =>
      participants.filter(
        (p) => p.removed_at == null && p.participant_role === "COACH"
      ),
    [participants]
  );

  const mentionMatches = useMemo(() => {
    if (mentionQuery == null) return [];
    const q = mentionQuery.toLowerCase();
    return coachOptions
      .filter((p) => p.name && p.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, coachOptions]);

  const notify = useCallback((message, type = "error") => {
    setToast(null);
    // force re-mount so repeated identical messages still show
    requestAnimationFrame(() => setToast({ message, type }));
  }, []);

  const resetComposer = useCallback(() => {
    setText("");
    setAttachment(null);
    setSelectedMentions({});
    setMentionQuery(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClearReply?.();
  }, [onClearReply]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    const caret = e.target.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const match = before.match(/@(\w*)$/);
    setMentionQuery(match ? match[1] : null);
  };

  const handlePickMention = (opt) => {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret).replace(/@(\w*)$/, `@${opt.name} `);
    const after = text.slice(caret);
    setText(before + after);
    // Prefer the normalized id (resolved_user_id) so the mentions payload
    // never carries the employee_id form — see commsParticipantId.js.
    setSelectedMentions((prev) => ({ ...prev, [opt.name]: participantId(opt) }));
    setMentionQuery(null);
    el?.focus();
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    const isImage = file.type.startsWith("image/");
    if (!isImage && !isPdf) {
      notify("Only images and PDFs can be attached.");
      return;
    }
    const limit = isPdf ? PDF_MAX_BYTES : IMAGE_MAX_BYTES;
    if (file.size > limit) {
      const mb = Math.round(limit / (1024 * 1024));
      notify(`File too large. Max ${mb}MB for ${isPdf ? "PDFs" : "images"}.`);
      return;
    }

    setUploading(true);
    try {
      const { width, height } = await probeImageSize(file);
      const { data } = await presignUpload(commsPost, {
        file_name: file.name,
        content_type: file.type,
        file_size: file.size,
        scope: "chat",
      });
      const putRes = await fetch(data.upload_url, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error("Upload failed. Please try again.");
      setAttachment({
        data_url: data.data_url,
        // The storage identity — required to read the bytes back later, since
        // data_url is not directly fetchable.
        blob_path: data.blob_path,
        file_size: file.size,
        content_type: contentTypeFromBucket(data.content_type_bucket),
        mime_type: file.type,
        name: file.name,
        ...(width ? { width, height } : {}),
      });
    } catch (err) {
      notify(err?.message || "Attachment upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearAttachment = () => setAttachment(null);

  const canSend =
    !hardDisabled && !sending && !uploading && (text.trim() || attachment);

  const handleSend = async () => {
    if (!canSend) return;
    // Only keep mentions whose @Name token remains in the text.
    const mentions = Object.entries(selectedMentions)
      .filter(([name]) => text.includes(`@${name}`))
      .map(([, userId]) => userId);

    const contentType = attachment ? attachment.content_type : "TEXT";
    const payload = {
      content: text.trim(),
      content_type: contentType,
      coach_only: isInternal,
      reply_to_id: replyTo?.id ?? null,
      idempotency_key: crypto.randomUUID(),
      mentions,
      // Plural form only — sending both `attachments` and the deprecated
      // singular `attachment` is a 422. content_type above must be the file
      // bucket to match: TEXT with attachments (or a bucket without) is a 400.
      attachments: attachment
        ? [
            {
              media: {
                data_url: attachment.data_url,
                blob_path: attachment.blob_path,
                mime_type: attachment.mime_type,
                media_kind: attachment.content_type,
                file_size: attachment.file_size,
                original_file_name: attachment.name,
                ...(attachment.width ? { width: attachment.width } : {}),
                ...(attachment.height ? { height: attachment.height } : {}),
              },
              attachment_purpose: "GENERAL",
            },
          ]
        : [],
    };

    setSending(true);
    try {
      await onSubmit(payload);
      resetComposer();
    } catch (err) {
      notify(err?.message || "Message could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && mentionQuery == null) {
      e.preventDefault();
      handleSend();
    }
  };

  const rows = Math.min(
    MAX_TEXTAREA_ROWS,
    Math.max(1, text.split("\n").length)
  );

  const placeholder = replyTo
    ? `Reply to ${replyTo.sender_name}`
    : isInternal
    ? "Internal note…"
    : "Message client";

  const inputTone = isInternal
    ? "bg-internal-bg border-internal text-internal-text"
    : "bg-white border-gray-200 text-gray-800";

  const notice = hardDisabled
    ? bannerText || "This room is archived. New messages are disabled."
    : bannerText;

  return (
    <div className="flex-shrink-0 border-t border-gray-200 bg-white px-2 py-2 sm:px-4 sm:py-3">
      <div className="mx-auto w-full max-w-3xl">
      {toast?.message && <Toaster message={toast.message} type={toast.type} />}

      {modes.length > 1 && (
        <div className="mb-2 flex gap-2" role="tablist" aria-label="Message mode">
          {modes.includes("client") && (
            <button
              type="button"
              role="tab"
              aria-selected={!isInternal}
              onClick={() => setMode("client")}
              disabled={hardDisabled}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                !isInternal
                  ? "bg-light-orange text-orange-primary"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Message client
            </button>
          )}
          {modes.includes("internal") && (
            <button
              type="button"
              role="tab"
              aria-selected={isInternal}
              onClick={() => setMode("internal")}
              disabled={hardDisabled}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                isInternal
                  ? "bg-internal-bg text-internal-text"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <FiLock aria-hidden="true" /> Internal note
            </button>
          )}
        </div>
      )}

      {notice && (
        <p className="mb-2 text-xs text-gray-500" role="status">
          {notice}
        </p>
      )}

      {replyTo && (
        <div className="mb-2 flex items-start justify-between gap-2 rounded-lg bg-gray-100 px-3 py-2">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-700">
              {replyTo.sender_name}
            </p>
            <p className="truncate text-xs text-gray-500">{replyTo.snippet}</p>
          </div>
          <button
            type="button"
            aria-label="Clear reply"
            onClick={onClearReply}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
          >
            <FiX />
          </button>
        </div>
      )}

      {attachment && (
        <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-light-orange px-3 py-1 text-xs text-orange-primary">
          <FiPaperclip aria-hidden="true" />
          <span className="max-w-[12rem] truncate">{attachment.name}</span>
          <button
            type="button"
            aria-label="Remove attachment"
            onClick={clearAttachment}
            className="text-orange-primary hover:opacity-70"
          >
            <FiX />
          </button>
        </div>
      )}

      {isInternal && (
        <p className="mb-1 inline-flex items-center gap-1 text-xs text-internal-text">
          <FiLock aria-hidden="true" /> Internal · hidden from client
        </p>
      )}

      <div className="relative flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          className="hidden"
          onChange={handleFilePick}
          aria-hidden="true"
          tabIndex={-1}
        />
        <button
          type="button"
          aria-label="Attach a file"
          onClick={() => fileInputRef.current?.click()}
          disabled={hardDisabled || uploading || !!attachment}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-40 sm:h-9 sm:w-9"
        >
          <FiPaperclip />
        </button>

        <div className="relative flex-1">
          <MentionMenu options={mentionMatches} onPick={handlePickMention} />
          <label htmlFor="composer-input" className="sr-only">
            Message text
          </label>
          <textarea
            id="composer-input"
            ref={textareaRef}
            rows={rows}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            disabled={hardDisabled}
            placeholder={placeholder}
            className={`w-full resize-none rounded-lg border px-3 py-2 text-base outline-none focus:ring-2 focus:ring-orange-primary/40 disabled:opacity-50 sm:text-sm ${inputTone}`}
          />
        </div>

        <Button
          text=""
          icon={
            sending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <FiSend />
            )
          }
          iconColor="text-white"
          onClick={handleSend}
          disabled={!canSend}
          className="h-10 flex-shrink-0 bg-orange-primary text-white sm:h-9"
        />
      </div>
      </div>
    </div>
  );
}

export default Composer;
